import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type ReactNode,
} from "react";
import { IS_CLICK_DOMAIN, getClickTheme, getUrlTheme, THEME_STORAGE_KEY } from "@/lib/env";

export type ThemeOption = "auto" | "dark" | "light";

// Yagona manba `@/lib/env` — Paynet kabi integratsiyalar ham shu kalitga yozadi.
const THEME_KEY = THEME_STORAGE_KEY;
const THEMES: ThemeOption[] = ["auto", "light", "dark"];

/** Telegram WebApp yoki system prefers-color-scheme dan haqiqiy rejimni aniqlaydi */
export function resolveTheme(theme: ThemeOption): "dark" | "light" {
  if (theme !== "auto") return theme;
  const tgScheme = window.Telegram?.WebApp?.colorScheme;
  if (tgScheme === "dark" || tgScheme === "light") return tgScheme;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyThemeToDOM(theme: ThemeOption) {
  document.documentElement.setAttribute("data-theme", resolveTheme(theme));
}

function getInitialTheme(): ThemeOption {
  // Super app URL parami (?theme=) eng yuqori ustuvorlik. Qo'llangach localStorage'ga
  // saqlaymiz — URL tozalangach yoki reload'da ham tanlangan mavzu saqlanib qolsin.
  const fromUrl = getUrlTheme();
  if (fromUrl) {
    try {
      localStorage.setItem(THEME_KEY, fromUrl);
    } catch {
      /* localStorage mavjud bo'lmasligi mumkin — e'tiborsiz qoldiramiz */
    }
    return fromUrl;
  }
  if (IS_CLICK_DOMAIN) {
    return getClickTheme() ?? "dark";
  }
  return (localStorage.getItem(THEME_KEY) as ThemeOption | null) ?? "auto";
}

type ThemeContextValue = {
  theme: ThemeOption;
  setTheme: (next: ThemeOption) => void;
  resolvedTheme: "dark" | "light";
  THEMES: typeof THEMES;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Barcha `useTheme()` chaqiruvlari bir xil holatni ko‘radi.
 * Oldin `App` va `ProfilePage` alohida `useState` ishlatgani uchun tema almashganda
 * `resolvedTheme` / `useBrandTokens` DOM bilan sinxron bo‘lmagan.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeOption>(getInitialTheme);
  const [autoResolveTick, bumpAutoResolved] = useReducer((n: number) => n + 1, 0);

  const setTheme = useCallback((next: ThemeOption) => {
    if (!IS_CLICK_DOMAIN) {
      localStorage.setItem(THEME_KEY, next);
    }
    setThemeState(next);
  }, []);

  useEffect(() => {
    applyThemeToDOM(theme);
  }, [theme]);

  useEffect(() => {
    if (IS_CLICK_DOMAIN) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onSystemChange = () => {
      const saved = localStorage.getItem(THEME_KEY) as ThemeOption | null;
      if ((saved ?? "auto") === "auto") {
        applyThemeToDOM("auto");
        bumpAutoResolved();
      }
    };
    mq.addEventListener("change", onSystemChange);
    return () => mq.removeEventListener("change", onSystemChange);
  }, []);

  const resolvedTheme = useMemo(() => {
    // autoResolveTick: system tema (prefers-color-scheme) o'zgarganda `theme`
    // o'zgarmaydi, lekin resolveTheme("auto") natijasi o'zgaradi — shuning uchun
    // tick ni o'qib, qayta hisoblashga majburlaymiz.
    void autoResolveTick;
    return resolveTheme(theme);
  }, [theme, autoResolveTick]);

  const value = useMemo(
    () => ({ theme, setTheme, resolvedTheme, THEMES }),
    [theme, setTheme, resolvedTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme() ThemeProvider ichida chaqirilishi kerak.");
  }
  return ctx;
}
